import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";
import { getALLproducts } from "../services/productService";
import { getcategorylist } from "../services/categoryService";

const IMG_BASE = import.meta.env.VITE_IMG_URL ?? "http://localhost:8000/storage";

const mapApiProduct = (p) => {
  const price = 20 + (p.name.charCodeAt(0) % 50);
  const oldPrice = price + 10;
  const primary = p.images?.find((img) => img.is_primary) || p.images?.[0];
  const img1 = primary
    ? primary.image.startsWith("http")
      ? primary.image
      : `${IMG_BASE}/${primary.image}`
    : "/img/product/placeholder.jpg";
  const second = p.images?.length > 1 ? p.images[1] : primary;
  const img2 = second
    ? second.image.startsWith("http")
      ? second.image
      : `${IMG_BASE}/${second.image}`
    : img1;
  const badge =
    p.name.charCodeAt(1) % 3 === 0
      ? ["Sale"]
      : p.name.charCodeAt(1) % 3 === 1
      ? ["Sale", "New"]
      : [];
  return {
    id: p.product_id,
    product_id: p.product_id,
    name: p.name,
    category: p.category?.name || "",
    category_id: p.category_id,
    price,
    oldPrice,
    img1,
    img2,
    badge,
    description: p.description,
    additional_information: p.additional_information,
    status: p.status,
    images: p.images || [],
  };
};

// Collect all descendant category IDs including the parent itself
const getDescendantIds = (catId, allCategories) => {
  const ids = [catId];
  const children = allCategories.filter(
    (c) => String(c.parent_id) === String(catId)
  );
  for (const child of children) {
    ids.push(...getDescendantIds(child.category_id, allCategories));
  }
  return ids;
};

function useResponsiveVisible() {
  const [visible, setVisible] = useState(
    typeof window !== "undefined" ? getVisibleCount(window.innerWidth) : 5
  );
  useEffect(() => {
    const handleResize = () => setVisible(getVisibleCount(window.innerWidth));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return visible;
}

function getVisibleCount(width) {
  if (width < 480) return 1;
  if (width < 640) return 2;
  if (width < 768) return 3;
  if (width < 1024) return 4;
  return 5;
}

export default function TrendingProducts() {
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const VISIBLE = useResponsiveVisible();

  useEffect(() => {
    const load = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          getALLproducts(),
          getcategorylist(),
        ]);

        const prodData = prodRes.data?.data || prodRes.data || [];
        const catData = catRes.data?.data || catRes.data || [];

        const mapped = prodData.map(mapApiProduct);
        setAllProducts(mapped);
        setAllCategories(catData);

        // Only show parent/top-level categories as tabs
        const parentCats = catData.filter((c) => !c.parent_id);
        setTabs(parentCats);
        console.log({parentCats});
        
        // Default: first parent category that has products
        const firstWithProducts = parentCats.find((cat) => {
          const ids = getDescendantIds(cat.category_id, catData);
          return mapped.some((p) => ids.includes(p.category_id));
        });
        setActiveTabId(firstWithProducts?.category_id ?? parentCats[0]?.category_id ?? null);
      } catch {
        // non-critical
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Filter products by active tab (includes subcategories)
  const filteredProducts = activeTabId
    ? (() => {
        const ids = getDescendantIds(activeTabId, allCategories);
        return allProducts.filter((p) => ids.includes(p.category_id));
      })()
    : allProducts;

  const list = filteredProducts.length > 0 ? filteredProducts : allProducts;
  const maxIndex = Math.max(0, list.length - VISIBLE);

  const handleTab = (catId) => {
    setActiveTabId(catId);
    setIndex(0);
  };

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

  if (loading) {
    return (
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 flex justify-center items-center min-h-[200px]">
          <span className="animate-spin border-4 border-gray-200 border-l-primary rounded-full w-10 h-10 inline-block"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Tabs */}
        <div className="flex flex-wrap justify-center items-center gap-3 mb-8">
          {/* "All" tab */}
          <button
            onClick={() => {
              setActiveTabId(null);
              setIndex(0);
            }}
            className={`text-xs font-bold uppercase tracking-widest transition-colors px-5 py-1.5 ${
              activeTabId === null
                ? "border border-[#8bc34a] text-[#8bc34a] rounded-full"
                : "text-gray-800 border border-transparent hover:text-[#8bc34a]"
            }`}
          >
            All
          </button>

          {tabs.map((tab) => {
            const ids = getDescendantIds(tab.category_id, allCategories);
            const hasProducts = allProducts.some((p) => ids.includes(p.category_id));
            if (!hasProducts) return null;
            return (
              <button
                key={tab.category_id}
                onClick={() => handleTab(tab.category_id)}
                className={`text-xs font-bold uppercase tracking-widest transition-colors px-5 py-1.5 ${
                  activeTabId === tab.category_id
                    ? "border border-[#8bc34a] text-[#8bc34a] rounded-full"
                    : "text-gray-800 border border-transparent hover:text-[#8bc34a]"
                }`}
              >
                {tab.name}
              </button>
            );
          })}
        </div>

        {list.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-10">No products found.</p>
        ) : (
          <div className="relative flex items-center">
            {/* Left Arrow */}
            <button
              onClick={prev}
              disabled={index === 0}
              className="absolute -left-2 md:-left-6 z-10 w-8 h-8 rounded-full bg-white/90 md:bg-transparent shadow-sm md:shadow-none flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
              aria-label="Previous"
            >
              <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                <path
                  d="M9 1L1 9L9 17"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div className="overflow-hidden w-full">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${index * (100 / VISIBLE)}%)` }}
              >
                {list.map((p) => (
                  <div
                    key={p.id}
                    className="flex-shrink-0 cursor-pointer"
                    style={{ width: `${100 / VISIBLE}%` }}
                    onClick={() => navigate(`/product-details/${p.product_id}`)}
                  >
                    <div className="px-2">
                      <ProductCard product={p} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={next}
              disabled={index >= maxIndex}
              className="absolute -right-2 md:-right-6 z-10 w-8 h-8 rounded-full bg-white/90 md:bg-transparent shadow-sm md:shadow-none flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
              aria-label="Next"
            >
              <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                <path
                  d="M1 1L9 9L1 17"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}